import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { waLink } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Search, Plus, MessageCircle } from "lucide-react";

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers?search=${encodeURIComponent(q)}`);
      setCustomers(res.data);
    } catch (e) { console.error("Failed to load customers:", e); } finally { setLoading(false); }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="customers-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Customers</h1>
        {user?.role !== "operator" && (
          <Button onClick={() => navigate("/new-job")} data-testid="customers-new-job-button" className="font-semibold">
            <Plus className="h-4 w-4 mr-1" /> New Job
          </Button>
        )}
      </div>
      <div className="relative mt-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="customers-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, mobile, email, city…" className="pl-9 h-11" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-testid="customers-grid">
        {customers.map((c) => (
          <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`customer-card-${c.id}`}>
            <CardContent className="p-4">
              <button className="w-full text-left" onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-open-${c.id}`}>
                <p className="font-semibold">{c.customer_name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{c.mobile} {c.city ? `· ${c.city}` : ""}</p>
                <p className="text-xs text-muted-foreground mt-1">{c.total_jobs} job{c.total_jobs === 1 ? "" : "s"}</p>
              </button>
              {waLink(c.whatsapp || c.mobile) && (
                <a href={waLink(c.whatsapp || c.mobile)} target="_blank" rel="noreferrer"
                  data-testid={`customer-whatsapp-${c.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900 rounded-full px-3 py-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/40">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp Customer
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {!loading && customers.length === 0 && (
        <p className="p-8 text-center text-sm text-muted-foreground" data-testid="customers-empty">No customers found.</p>
      )}
    </div>
  );
}
